import { CheckIcon, RotateCcwIcon } from "lucide-react";
import { useState } from "react";
import type { CmmdProjectAgentMember } from "@cmmd-forge/contracts";

import {
  AgentMemberAvatar,
  generatedAgentAvatarUrl,
  getAgentMemberAvatarSeed,
} from "~/components/identity/AgentMemberIdentity";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";

const AVATAR_VARIANTS = ["atlas", "ember", "orbit", "signal", "vector", "nova"] as const;

export function AgentMemberAvatarPicker(props: {
  readonly member: CmmdProjectAgentMember;
  readonly disabled: boolean;
  readonly onSave: (
    avatar: { readonly kind: "generated"; readonly seed: string } | { readonly kind: "default" },
  ) => Promise<void>;
}) {
  const currentSeed = getAgentMemberAvatarSeed(props.member);
  const [selectedSeed, setSelectedSeed] = useState<string | null>(currentSeed);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">("idle");
  const options = AVATAR_VARIANTS.map((variant) => `${props.member.agentId}:${variant}`);

  if (props.member.agentKind !== "forge") {
    return (
      <section className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3">
        <AgentMemberAvatar member={props.member} className="size-10" />
        <div className="min-w-0">
          <h4 className="text-sm font-medium">Provider-managed identity</h4>
          <p className="text-xs leading-5 text-muted-foreground">
            This avatar is the canonical {props.member.agentKind} mark and cannot be replaced.
          </p>
        </div>
      </section>
    );
  }

  const save = async () => {
    setSaveStatus("saving");
    try {
      await props.onSave(
        selectedSeed ? { kind: "generated", seed: selectedSeed } : { kind: "default" },
      );
      setSaveStatus("saved");
    } catch {
      setSaveStatus("idle");
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h4 className="text-sm font-medium">Agent avatar</h4>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Choose the identity used across Forge.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          disabled={props.disabled || saveStatus === "saving"}
          onClick={() => {
            setSelectedSeed(null);
            setSaveStatus("idle");
          }}
        >
          <RotateCcwIcon className="size-3.5" aria-hidden />
          Reset
        </Button>
      </div>
      <div className="flex flex-wrap gap-2" aria-label="Generated avatar choices">
        {options.map((seed) => (
          <button
            key={seed}
            type="button"
            aria-label={`Select generated avatar ${seed.split(":").at(-1)}`}
            aria-pressed={selectedSeed === seed}
            className={cn(
              "relative size-14 overflow-hidden rounded-md border bg-muted transition-colors duration-150 motion-reduce:transition-none hover:border-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              selectedSeed === seed ? "border-primary ring-1 ring-primary" : "border-border",
            )}
            onClick={() => {
              setSelectedSeed(seed);
              setSaveStatus("idle");
            }}
          >
            <img src={generatedAgentAvatarUrl(seed)} alt="" className="size-full object-cover" />
            {selectedSeed === seed ? (
              <span className="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <CheckIcon className="size-2.5" aria-hidden />
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
        <span aria-live="polite" className="text-xs text-muted-foreground">
          {saveStatus === "saved" ? "Avatar saved" : "Changes apply everywhere this agent appears."}
        </span>
        <Button
          size="sm"
          disabled={props.disabled || saveStatus === "saving" || selectedSeed === currentSeed}
          onClick={() => void save()}
        >
          {saveStatus === "saving" ? "Saving…" : "Save"}
        </Button>
      </div>
    </section>
  );
}
